import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import { db, functions } from './firebase';
import logo from './ap-logo-4.png';
import './VolunteerRegistration.css';

const initialForm = {
  firstName: '', lastName: '', email: '', phone: '',
  emergencyContactName: '', emergencyContactPhone: '',
  availabilityNotes: '', fsaLevel2Confirmed: false, consent: false,
};

const formatSlot = (slot) => {
  const date = slot.date || 'Date to be confirmed';
  const time = [slot.startTime, slot.endTime].filter(Boolean).join(' – ');
  return time ? `${date}, ${time}` : date;
};

const placesLeft = (slot) => Math.max(Number(slot.capacity || 0) - Number(slot.bookedCount || 0), 0);

const TrustIcon = ({ d }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7ab648" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d}/>
  </svg>
);

const VolunteerRegistration = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [pageContent, setPageContent] = useState({
    image_url: '',
    eyebrow: 'Volunteer with Akshaya Patra UK',
    headline: 'Give a morning.\nFeed a child.',
    subtitle: 'Join our kitchen team and help prepare nutritious meals for children during school holidays through the government-funded HAF programme.',
  });
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [pageSnap, slotSnap] = await Promise.all([
          getDoc(doc(db, 'haf_content', 'volunteers')),
          getDocs(query(collection(db, 'volunteerSlots'), where('status', '==', 'open'))),
        ]);
        if (pageSnap.exists() && active) setPageContent(p => ({ ...p, ...pageSnap.data() }));
        if (active) {
          const items = slotSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(s => placesLeft(s) > 0)
            .sort((a, b) => `${a.date || ''}${a.startTime || ''}`.localeCompare(`${b.date || ''}${b.startTime || ''}`));
          setSlots(items);
          setSelectedSlotId(items[0]?.id || '');
        }
      } catch (err) {
        if (active) setError('Could not load volunteer slots. Please try again later.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const selectedSlot = useMemo(() => slots.find(s => s.id === selectedSlotId), [slots, selectedSlotId]);
  const isRequired = (field) => selectedSlot?.formFields?.[field] !== false;
  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!selectedSlot) { setError('Please choose an available volunteer slot.'); return; }
    if (!executeRecaptcha) { setError('Security check still loading — please wait a moment.'); return; }
    setSubmitting(true);
    try {
      const captchaToken = await executeRecaptcha('volunteer_registration');
      const result = await httpsCallable(functions, 'registerVolunteer')({ slotId: selectedSlot.id, ...form, captchaToken });
      setSuccess(result.data?.message || 'Registration confirmed. Please check your email for details.');
      setForm(initialForm);
      setSelectedSlotId('');
      setSlots(prev => prev
        .map(s => s.id === selectedSlot.id ? { ...s, bookedCount: Number(s.bookedCount || 0) + 1 } : s)
        .filter(s => placesLeft(s) > 0));
    } catch (err) {
      setError(err.message || 'Registration could not be completed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const headline = pageContent.headline || '';

  return (
    <div className="vol-page">

      {/* NAV */}
      <nav className="vol-nav">
        <button type="button" className="vol-logo-btn" onClick={() => navigate('/staging')} aria-label="Back to home">
          <img src={logo} alt="Akshaya Patra UK" />
        </button>
        <button type="button" className="vol-back-btn" onClick={() => navigate('/staging')}>
          ← Back to HAF
        </button>
      </nav>

      {/* HERO */}
      <div className="vol-hero">
        {pageContent.image_url
          ? <img src={pageContent.image_url} className="vol-hero-img" alt="Volunteers at Akshaya Patra UK" />
          : <div className="vol-hero-ph" />
        }
        <div className="vol-hero-overlay">
          <p className="vol-eyebrow">{pageContent.eyebrow}</p>
          <h1 className="vol-hero-title">
            {headline.split('\n').map((line, i, arr) => (
              <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
            ))}
          </h1>
          {pageContent.subtitle && <p className="vol-hero-sub">{pageContent.subtitle}</p>}
        </div>
      </div>

      {/* TRUST STRIP */}
      <div className="vol-trust">
        <div className="vol-trust-inner">
          <div className="vol-trust-item">
            <TrustIcon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            FSA 5-Star Rated Kitchen
          </div>
          <div className="vol-trust-item">
            <TrustIcon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <TrustIcon d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
            Community volunteers welcome
          </div>
          <div className="vol-trust-item">
            <TrustIcon d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
            Flexible morning shifts
          </div>
          <div className="vol-trust-item">
            <TrustIcon d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"/>
            Government-funded HAF programme
          </div>
        </div>
      </div>

      {/* MAIN BODY */}
      <div className="vol-body">
        <div className="vol-grid">

          {/* SLOTS PANEL */}
          <div className="vol-panel">
            <div className="vol-panel-head">
              <span className="vol-panel-head-title">Available Slots</span>
              {!loading && <span className="vol-panel-head-count">{slots.length} open</span>}
            </div>

            {loading && <div className="vol-empty"><span>Loading volunteer slots…</span></div>}

            {!loading && slots.length === 0 && (
              <div className="vol-empty">
                <strong>No open slots right now.</strong>
                <span>Please check back soon or get in touch with the team for upcoming opportunities.</span>
              </div>
            )}

            {!loading && slots.length > 0 && (
              <div className="vol-slot-list">
                {slots.map(slot => (
                  <button
                    type="button" key={slot.id}
                    className={`vol-slot-card${selectedSlotId === slot.id ? ' selected' : ''}`}
                    onClick={() => setSelectedSlotId(slot.id)}
                  >
                    <span className="vol-slot-title">{slot.title}</span>
                    <span className="vol-slot-date">{formatSlot(slot)}</span>
                    <span className="vol-slot-location">📍 {slot.location}</span>
                    <span className="vol-slot-meta">{placesLeft(slot)} {placesLeft(slot) === 1 ? 'place' : 'places'} left</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* FORM PANEL */}
          <div className="vol-panel">
            <div className="vol-panel-head">
              <span className="vol-panel-head-title">Register</span>
              {selectedSlot && <span style={{ fontSize: '.75rem', color: '#7a8fa6' }}>{formatSlot(selectedSlot)}</span>}
            </div>

            <form className="vol-form-inner" onSubmit={handleSubmit}>

              {selectedSlot && (
                <div className="vol-slot-summary">
                  <div className="vol-slot-summary-label">Selected slot</div>
                  <div className="vol-slot-summary-title">{selectedSlot.title}</div>
                  <div className="vol-slot-summary-when">{formatSlot(selectedSlot)} · {selectedSlot.location}</div>
                </div>
              )}

              {selectedSlot?.requirements && (
                <div className="vol-requirements">
                  <strong>Requirements</strong>
                  {selectedSlot.requirements}
                </div>
              )}

              {selectedSlot?.requireFsaLevel2 && (
                <label className="vol-consent-field">
                  <input type="checkbox" checked={form.fsaLevel2Confirmed} onChange={e => updateField('fsaLevel2Confirmed', e.target.checked)} required />
                  <span>I confirm I hold Food Safety / FSA Level 2 (or equivalent) certification.</span>
                </label>
              )}

              <div className="vol-two-col">
                <div className="vol-field">
                  <label>First name</label>
                  <input value={form.firstName} onChange={e => updateField('firstName', e.target.value)} required={isRequired('firstName')} placeholder="Jane" />
                </div>
                <div className="vol-field">
                  <label>Last name</label>
                  <input value={form.lastName} onChange={e => updateField('lastName', e.target.value)} required={isRequired('lastName')} placeholder="Smith" />
                </div>
              </div>

              <div className="vol-field">
                <label>Email address</label>
                <input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} required={isRequired('email')} placeholder="jane@example.com" />
              </div>

              <div className="vol-field">
                <label>Phone number</label>
                <input type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)} required={isRequired('phone')} placeholder="+44 7700 000000" />
              </div>

              <div className="vol-two-col">
                <div className="vol-field">
                  <label>Emergency contact name</label>
                  <input value={form.emergencyContactName} onChange={e => updateField('emergencyContactName', e.target.value)} required={isRequired('emergencyContactName')} placeholder="Full name" />
                </div>
                <div className="vol-field">
                  <label>Emergency contact phone</label>
                  <input type="tel" value={form.emergencyContactPhone} onChange={e => updateField('emergencyContactPhone', e.target.value)} required={isRequired('emergencyContactPhone')} placeholder="+44 7700 000000" />
                </div>
              </div>

              <div className="vol-field">
                <label>Availability notes <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#9aabb8' }}>(optional)</span></label>
                <textarea rows={3} value={form.availabilityNotes} onChange={e => updateField('availabilityNotes', e.target.value)} required={isRequired('availabilityNotes')} placeholder="Any preferred duties, access requirements, or anything the team should know." />
              </div>

              <label className="vol-consent-field">
                <input type="checkbox" checked={form.consent} onChange={e => updateField('consent', e.target.checked)} required />
                <span>I agree that Akshaya Patra UK may contact me about this volunteer registration and related activities.</span>
              </label>

              {error && <div className="vol-message error">{error}</div>}
              {success && <div className="vol-message success">{success}</div>}

              <button type="submit" className="vol-submit" disabled={submitting || !selectedSlot}>
                {submitting ? 'Registering…' : 'Reserve my place'}
              </button>

              <p style={{ marginTop: 14, fontSize: '.72rem', color: '#9aabb8', textAlign: 'center', lineHeight: 1.6 }}>
                A confirmation email will be sent to you automatically.<br />
                Questions? Email <a href="mailto:nagashayana.reddy@akshayapatra.org.uk" style={{ color: '#4a7c2f' }}>nagashayana.reddy@akshayapatra.org.uk</a>
              </p>
            </form>
          </div>

        </div>
      </div>

      {/* FOOTER */}
      <footer className="vol-footer">
        © {new Date().getFullYear()} Akshaya Patra UK — Holiday Activities &amp; Food Programme &nbsp;·&nbsp;
        <a href="https://akshayapatramealorders.co.uk">akshayapatramealorders.co.uk</a>
      </footer>

    </div>
  );
};

export default VolunteerRegistration;
